/**
 * Safe localStorage wrapper with error handling
 * Handles private browsing mode and storage quota issues
 */
class StorageUtility {
  /**
   * Check if localStorage is available
   * @returns {boolean} True if localStorage is accessible
   */
  static isAvailable() {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Get item from localStorage with error handling
   * @param {string} key - The key to retrieve
   * @param {*} defaultValue - Default value if key doesn't exist or storage unavailable
   * @returns {string|null} The stored value or default value
   */
  static get(key, defaultValue = null) {
    try {
      if (!this.isAvailable()) {
        console.warn('localStorage is not available (private browsing?)');
        return defaultValue;
      }
      const value = localStorage.getItem(key);
      return value !== null ? value : defaultValue;
    } catch (e) {
      console.warn(`Error reading from localStorage [${key}]:`, e);
      return defaultValue;
    }
  }

  /**
   * Set item in localStorage with error handling
   * @param {string} key - The key to set
   * @param {string} value - The value to store
   * @returns {boolean} True if successful, false otherwise
   */
  static set(key, value) {
    try {
      if (!this.isAvailable()) {
        console.warn('localStorage is not available (private browsing?)');
        return false;
      }
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      console.warn(`Error writing to localStorage [${key}]:`, e);
      return false;
    }
  }

  /**
   * Remove item from localStorage with error handling
   * @param {string} key - The key to remove
   * @returns {boolean} True if successful, false otherwise
   */
  static remove(key) {
    try {
      if (!this.isAvailable()) {
        console.warn('localStorage is not available (private browsing?)');
        return false;
      }
      localStorage.removeItem(key);
      return true;
    } catch (e) {
      console.warn(`Error removing from localStorage [${key}]:`, e);
      return false;
    }
  }

  /**
   * Get and validate gender preference
   * @returns {string|null} 'man', 'woman', or null if invalid/not set
   */
  static getGenderPreference() {
    const VALID_GENDERS = ['man', 'woman'];
    const saved = this.get('headerTab');

    if (saved && !VALID_GENDERS.includes(saved)) {
      console.warn(`Invalid gender preference: ${saved}. Removing.`);
      this.remove('headerTab');
      return null;
    }

    return saved;
  }

  /**
   * Set gender preference with validation
   * @param {string} gender - 'man' or 'woman'
   * @returns {boolean} True if successful
   */
  static setGenderPreference(gender) {
    const VALID_GENDERS = ['man', 'woman'];

    if (!VALID_GENDERS.includes(gender)) {
      console.error(`Invalid gender preference: ${gender}`);
      return false;
    }

    return this.set('headerTab', gender);
  }
}
